import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import type { Schedule } from '@/types'
import { useScheduleStore } from '@/stores/scheduleStore'
import { ownRowShifts } from '@/utils/otherDepartmentShifts'

const toNumber = (value: unknown) => {
    if (value === null || value === undefined || value === '') return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
}

// Показатели сотрудника за месяц графика (прежние источники EmployeeStats).
// null — значение неизвестно (показатели не загружены, нет записи по ТУ); это не ноль.
// requireCurrentContext — только показатели загруженного текущего месяца (ошибка/загрузка → null),
// без прежнего месяца под названием нового. EmployeeStats опцию не использует (прежняя семантика).
export function useEmployeeStats(scheduleSource: MaybeRefOrGetter<Schedule>, options: { requireCurrentContext?: boolean } = {}) {
    const store = useScheduleStore()
    const schedule = computed(() => toValue(scheduleSource))

    const getStatValue = (statKey: string) => {
        if (options.requireCurrentContext && !store.isStatsContextLoaded) return null
        const values = store.stats?.[statKey]?.values
        if (!Array.isArray(values)) return null

        const entry = values.find((item: any) => item.employment_id === schedule.value.id)
        return entry ? toNumber(entry.value) : null
    }

    const planHours = computed(() => getStatValue('plan_hours'))
    const extraHours = computed(() => getStatValue('additional_hours'))
    const mainHours = computed(() => (
        planHours.value === null || extraHours.value === null ? null : planHours.value - extraHours.value
    ))
    // Показатели строки — по собственным сменам: ТУ строки в выбранном подразделении (RD-176/RD-182).
    const ownShifts = computed(() => ownRowShifts(schedule.value, store.currentDepartment?.id))
    const moderate = computed(() => ownShifts.value.filter(item => item.violation?.general_status === 'moderate').length)
    const warning = computed(() => ownShifts.value.filter(item => item.violation?.general_status === 'warning').length)
    const salary = computed(() => toNumber(schedule.value.salary_hour))
    const shiftsNumber = computed(() => ownShifts.value.length)
    const norm = computed(() => toNumber(schedule.value.hours_norm))

    return { planHours, extraHours, mainHours, moderate, warning, salary, shiftsNumber, norm }
}
